import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
  collection, query, where, orderBy, onSnapshot,
  doc, updateDoc, arrayUnion, arrayRemove, getDoc
} from "firebase/firestore";
import { useApp } from "../App";
import { IconSearch, IconHeart, IconMarketLogo } from "../components/Icons";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "furnitureHome", label: "Furniture & Home" },
  { key: "bikesSports", label: "Bikes & Sports" },
  { key: "kidsClothing", label: "Kids' Clothing & Shoes" },
  { key: "electronics", label: "Electronics" },
  { key: "booksToys", label: "Books & Toys" },
  { key: "babyToddler", label: "Baby & Toddler" },
  { key: "other", label: "Other" },
];

const SIZE_OPTIONS = [
  { group: "By age", sizes: [
    "0–3 months","3–6 months","6–9 months","9–12 months",
    "12–18 months","18–24 months","2–3 years","3–4 years",
    "5–6 years","7–8 years","9–10 years","11–12 years","13–14 years",
  ]},
  { group: "By EU clothing size", sizes: [
    "44 cm","50 cm","56 cm","62 cm","68 cm","74 cm","80 cm",
    "86 cm","92 cm","98 cm","104 cm","110 cm","116 cm","122 cm",
    "128 cm","134 cm","140 cm","146 cm","152 cm","158 cm","164 cm",
  ]},
  { group: "By EU shoe size", sizes: [
    "EU 16","EU 17","EU 18","EU 19","EU 20",
    "EU 21","EU 22","EU 23","EU 24","EU 25",
    "EU 26","EU 27","EU 28","EU 29","EU 30",
    "EU 31","EU 32","EU 33","EU 34","EU 35",
    "EU 36","EU 37","EU 38","EU 39",
  ]},
];

export default function Browse() {
  const { user, t, openListing } = useApp();
  const [listings, setListings] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSizePanel, setShowSizePanel] = useState(false);
  const [selectedSize, setSelectedSize] = useState("");
  const sizePanelRef = useRef(null);

  useEffect(() => {
    const q = query(
      collection(db, "market_listings"),
      where("status", "==", "active"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setListings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchSaved = async () => {
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        setSaved((snap.data().saved || []).filter((id) => id.startsWith("m_")));
      }
    };
    fetchSaved();
  }, [user]);

  // Close size panel when category changes away from kidsClothing
  useEffect(() => {
    if (category !== "kidsClothing") {
      setShowSizePanel(false);
      setSelectedSize("");
    }
  }, [category]);

  const toggleSave = async (e, listingId) => {
    e.stopPropagation();
    const marketId = `m_${listingId}`;
    const userRef = doc(db, "users", user.uid);
    if (saved.includes(marketId)) {
      await updateDoc(userRef, { saved: arrayRemove(marketId) });
      setSaved((prev) => prev.filter((id) => id !== marketId));
    } else {
      await updateDoc(userRef, { saved: arrayUnion(marketId) });
      setSaved((prev) => [...prev, marketId]);
    }
  };

  const filtered = listings.filter((l) => {
    const matchesCategory = category === "all" || l.category === category;
    const matchesSearch =
      search === "" ||
      l.title?.toLowerCase().includes(search.toLowerCase()) ||
      l.location?.toLowerCase().includes(search.toLowerCase());
    const matchesSize =
      !selectedSize || l.size === selectedSize;
    return matchesCategory && matchesSearch && matchesSize;
  });

  const formatPrice = (price) => {
    if (price === 0 || price === "0") return "Free";
    return `€${price}`;
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-row">
          <div className="page-header-logo">
            <IconMarketLogo />
            <span className="page-header-wordmark">kepti market</span>
          </div>
        </div>
        <div className="search-bar">
          <IconSearch />
          <input
            type="text"
            placeholder={t.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Category filter strip */}
      <div className="filter-strip">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            className={`filter-chip ${category === cat.key ? "active" : ""}`}
            onClick={() => setCategory(cat.key)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Size filter button — only when Kids' Clothing is selected */}
      {category === "kidsClothing" && (
        <div style={{ padding: "4px 16px 0" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className={`filter-chip ${showSizePanel || selectedSize ? "active" : ""}`}
              onClick={() => setShowSizePanel((prev) => !prev)}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              {selectedSize ? selectedSize : t.filterBySize}
              <span style={{ fontSize: 10 }}>{showSizePanel ? "▲" : "▼"}</span>
            </button>
            {selectedSize && (
              <button
                className="filter-chip"
                onClick={() => { setSelectedSize(""); setShowSizePanel(false); }}
                style={{ color: "var(--text-tertiary)" }}
              >
                {t.clearSize}
              </button>
            )}
          </div>

          {/* Size panel */}
          {showSizePanel && (
            <div
              ref={sizePanelRef}
              style={{
                background: "var(--white)",
                borderRadius: 12,
                marginTop: 8,
                padding: "16px",
                boxShadow: "0 4px 20px rgba(26,39,68,0.12)",
                border: "1px solid var(--border)",
              }}
            >
              {SIZE_OPTIONS.map((group, gi) => (
                <div key={group.group}>
                  {gi > 0 && (
                    <div style={{
                      borderTop: "1px solid var(--border)",
                      margin: "12px 0",
                    }} />
                  )}
                  <div style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-tertiary)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    marginBottom: 10,
                  }}>
                    {group.group}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {group.sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => {
                          setSelectedSize(size === selectedSize ? "" : size);
                          setShowSizePanel(false);
                        }}
                        style={{
                          padding: "6px 12px",
                          borderRadius: 20,
                          fontSize: 13,
                          fontWeight: 500,
                          border: selectedSize === size
                            ? "1.5px solid var(--navy)"
                            : "1.5px solid var(--border)",
                          background: selectedSize === size
                            ? "var(--navy)"
                            : "var(--offwhite)",
                          color: selectedSize === size
                            ? "var(--cream)"
                            : "var(--text-secondary)",
                        }}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Listings */}
      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">
            {search || category !== "all" || selectedSize
              ? t.noListingsSearch
              : t.noListings}
          </div>
        </div>
      ) : (
        <div className="listings-feed">
          {filtered.map((listing) => (
            <div
              key={listing.id}
              className="listing-card"
              onClick={() => openListing(listing)}
            >
              {listing.imageUrl ? (
                <img
                  className="listing-card-image"
                  src={listing.imageUrl}
                  alt={listing.title}
                />
              ) : (
                <div className="listing-card-image-placeholder" />
              )}
              <button
                className="listing-card-heart"
                onClick={(e) => toggleSave(e, listing.id)}
              >
                <IconHeart filled={saved.includes(`m_${listing.id}`)} />
              </button>
              <div className="listing-card-body">
                <div className="listing-card-title">{listing.title}</div>
                <div className="listing-card-price">{formatPrice(listing.price)}</div>
                <div className="listing-card-meta">{listing.location}</div>
                {listing.movingSale && (
                  <div className="moving-sale-tag">{t.movingSale}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
